import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, BookOpen, Loader } from 'lucide-react';

/*
  BookReader – landscape two-page spread with a realistic corner-peel
  page-turn. Matches the reference image provided by user.
*/

const SCALE = 1.8;

export default function BookReader({ pdfUrl, title, subtitle, onClose }) {
  const [pdf, setPdf]             = useState(null);
  const [numPages, setNumPages]   = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [images, setImages]       = useState({});
  const [loading, setLoading]     = useState(true);
  const [peelProgress, setPeelProgress] = useState(0);
  const [peelCorner, setPeelCorner]     = useState('br');
  const [isFlipping, setIsFlipping]     = useState(false);
  const [flipDir, setFlipDir]           = useState('next');

  const bookRef = useRef(null);
  const cache   = useRef({});

  // ── Load PDF ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;
        const doc = await pdfjsLib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
        setLoading(false);
        [1, 2, 3, 4].forEach(p => renderPage(doc, p));
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  const renderPage = useCallback(async (doc, pageNum) => {
    if (!doc || pageNum < 1 || pageNum > doc.numPages) return;
    if (cache.current[pageNum]) return;
    const page   = await doc.getPage(pageNum);
    const vp     = page.getViewport({ scale: SCALE });
    const canvas = document.createElement('canvas');
    canvas.width  = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    const url = canvas.toDataURL();
    cache.current[pageNum] = url;
    setImages(prev => ({ ...prev, [pageNum]: url }));
  }, []);

  // Pre-render neighbours
  useEffect(() => {
    if (!pdf) return;
    const { left, right } = pagesAt(pageIndex, numPages);
    [left, right, (right || 0) + 1, (right || 0) + 2, (left || 0) - 1]
      .filter(Boolean)
      .forEach(p => renderPage(pdf, p));
  }, [pageIndex, pdf, numPages, renderPage]);

  // ── Page maths ────────────────────────────────────────────────────────────
  function pagesAt(idx, total) {
    if (idx === 0) return { left: null, right: 1 };
    const left  = idx * 2;
    const right = idx * 2 + 1;
    return { left: left <= total ? left : null, right: right <= total ? right : null };
  }

  const maxIndex = Math.ceil((numPages - 1) / 2);
  const { left: leftPage, right: rightPage } = pagesAt(pageIndex, numPages);

  // ── Navigate ──────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (isFlipping || pageIndex >= maxIndex) return;
    setFlipDir('next');
    setIsFlipping(true);
    setPeelProgress(0);
    setTimeout(() => { setPageIndex(i => i + 1); setIsFlipping(false); }, 550);
  }, [isFlipping, pageIndex, maxIndex]);

  const goPrev = useCallback(() => {
    if (isFlipping || pageIndex <= 0) return;
    setFlipDir('prev');
    setIsFlipping(true);
    setPeelProgress(0);
    setTimeout(() => { setPageIndex(i => i - 1); setIsFlipping(false); }, 550);
  }, [isFlipping, pageIndex]);

  // ── Mouse corner peel ─────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    if (!bookRef.current || isFlipping) return;
    const rect = bookRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;
    const sz = w * 0.15;

    if (x > w - sz && y > h - sz && pageIndex < maxIndex) {
      const dist = Math.sqrt((w - x) ** 2 + (h - y) ** 2);
      setPeelCorner('br');
      setPeelProgress(Math.max(0, Math.min(1, 1 - dist / (sz * 1.4))));
    } else if (x < sz && y > h - sz && pageIndex > 0) {
      const dist = Math.sqrt(x ** 2 + (h - y) ** 2);
      setPeelCorner('bl');
      setPeelProgress(Math.max(0, Math.min(1, 1 - dist / (sz * 1.4))));
    } else {
      setPeelProgress(0);
    }
  }, [isFlipping, pageIndex, maxIndex]);

  const handleClick = (e) => {
    if (!bookRef.current || isFlipping) return;
    const rect = bookRef.current.getBoundingClientRect();
    if (e.clientX - rect.left > rect.width / 2) goNext();
    else goPrev();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onClose]);

  const foldSize = peelProgress * 90;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#111] select-none">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-3 bg-black/60 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <BookOpen size={18} className="text-gold" />
          <div>
            <p className="text-white font-semibold text-sm leading-none">{title}</p>
            {subtitle && <p className="text-white/40 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/40 text-xs">
            {numPages > 0 && `Page ${rightPage || leftPage} of ${numPages}`}
          </span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center gap-6 px-6 py-6 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center gap-4 text-white/50">
            <Loader size={36} className="animate-spin text-gold" />
            <p className="text-sm">Loading newsletter…</p>
          </div>
        ) : (
          <>
            {/* Left Arrow */}
            <button
              onClick={goPrev}
              disabled={pageIndex === 0 || isFlipping}
              className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${pageIndex === 0 ? 'opacity-20 cursor-not-allowed bg-white/5' : 'bg-white/10 hover:bg-gold hover:text-navy text-white'}`}
            >
              <ChevronLeft size={22} />
            </button>

            {/* Book spread */}
            <div
              ref={bookRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setPeelProgress(0)}
              onClick={handleClick}
              className="relative cursor-pointer"
              style={{
                width:  'min(920px, 90vw)',
                height: 'min(580px, 72vh)',
                boxShadow: '0 40px 100px rgba(0,0,0,0.85)',
                borderRadius: 4,
              }}
            >
              {/* ── Left page ── */}
              <div
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ right: '50%', borderRadius: '4px 0 0 4px', background: '#1e1e1e' }}
              >
                {pageIndex === 0 ? (
                  <div className="w-full h-full bg-[#161616] flex items-center justify-center">
                    <BookOpen size={52} className="text-gold/15" />
                  </div>
                ) : leftPage && images[leftPage] ? (
                  <img src={images[leftPage]} alt={`p${leftPage}`} className="w-full h-full object-cover object-right" draggable={false} />
                ) : (
                  <PageSkeleton />
                )}
                {leftPage && (
                  <span className="absolute bottom-3 left-5 text-[11px] text-white/25 pointer-events-none font-medium">
                    Page {leftPage} of {numPages}
                  </span>
                )}
                {/* right spine shadow */}
                <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-black/50 to-transparent pointer-events-none" />
                {/* Bottom-left peel */}
                {peelCorner === 'bl' && peelProgress > 0 && (
                  <CornerPeel size={foldSize} corner="bl" />
                )}
              </div>

              {/* ── Spine ── */}
              <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[6px] z-20"
                style={{ background: 'linear-gradient(to right, #000, #555, #000)' }} />

              {/* ── Right page ── */}
              <div
                className="absolute inset-y-0 right-0 overflow-hidden"
                style={{
                  left: '50%',
                  borderRadius: '0 4px 4px 0',
                  background: '#f2ead8',
                  transformOrigin: 'left center',
                  transform: isFlipping && flipDir === 'next'
                    ? 'perspective(1200px) rotateY(-180deg)'
                    : 'none',
                  transition: isFlipping
                    ? 'transform 0.55s cubic-bezier(0.645,0.045,0.355,1)'
                    : 'none',
                  zIndex: isFlipping ? 30 : 1,
                }}
              >
                {rightPage && images[rightPage] ? (
                  <img src={images[rightPage]} alt={`p${rightPage}`} className="w-full h-full object-cover object-left" draggable={false} />
                ) : rightPage ? (
                  <PageSkeleton light />
                ) : (
                  <div className="w-full h-full bg-[#161616]" />
                )}
                {rightPage && (
                  <span className="absolute bottom-3 right-5 text-[11px] text-black/30 pointer-events-none font-medium">
                    Page {rightPage} of {numPages}
                  </span>
                )}
                {/* left spine shadow */}
                <div className="absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-black/25 to-transparent pointer-events-none" />
                {/* Bottom-right peel */}
                {peelCorner === 'br' && peelProgress > 0 && (
                  <CornerPeel size={foldSize} corner="br" />
                )}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={goNext}
              disabled={pageIndex >= maxIndex || isFlipping}
              className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${pageIndex >= maxIndex ? 'opacity-20 cursor-not-allowed bg-white/5' : 'bg-white/10 hover:bg-gold hover:text-navy text-white'}`}
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {/* Dot nav */}
      {!loading && (
        <div className="flex items-center justify-center gap-2 pb-5 shrink-0">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => !isFlipping && setPageIndex(i)}
              className={`rounded-full transition-all duration-300 ${pageIndex === i ? 'bg-gold w-5 h-2' : 'bg-white/20 hover:bg-white/40 w-2 h-2'}`}
            />
          ))}
        </div>
      )}

      {!loading && (
        <p className="text-center text-white/15 text-[11px] pb-4 shrink-0">
          Hover bottom corner to peel · Click left / right to turn · ← → keys
        </p>
      )}
    </div>
  );
}

/* Gold corner-peel triangle */
function CornerPeel({ size, corner }) {
  const isRight = corner === 'br';
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        [isRight ? 'right' : 'left']: 0,
        width: size,
        height: size,
        zIndex: 40,
        pointerEvents: 'none',
        transition: 'width 0.08s ease, height 0.08s ease',
      }}
    >
      {/* shadow under fold */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isRight
          ? 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.35))'
          : 'linear-gradient(225deg, transparent 50%, rgba(0,0,0,0.35))',
      }} />
      {/* golden fold flap */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, #e0c47a, #9a7220)',
        clipPath: isRight
          ? 'polygon(100% 0, 100% 100%, 0 100%)'
          : 'polygon(0 0, 100% 100%, 0 100%)',
        boxShadow: isRight
          ? '-6px -6px 16px rgba(0,0,0,0.5)'
          : '6px -6px 16px rgba(0,0,0,0.5)',
      }} />
    </div>
  );
}

function PageSkeleton({ light }) {
  const bg   = light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const base = light ? '#f2ead8' : '#1e1e1e';
  return (
    <div className="w-full h-full animate-pulse flex flex-col gap-3 p-10" style={{ background: base }}>
      {[0.5, 1, 0.8, 1, 0.7].map((w, i) => (
        <div key={i} className="h-3 rounded" style={{ width: `${w * 100}%`, background: bg }} />
      ))}
    </div>
  );
}
